/**
 * Flying with thumbs.
 *
 * Aiming is the screen — one finger turns the ship, two look around — and
 * everything a hand has to *hold* is here, in one grid under the left thumb.
 *
 * ── Why a grid and not scattered buttons ────────────────────────────────────
 *
 * Because a thumb does not hunt. Controls spread around the edge of a phone look
 * organised and are unusable in motion: each one is a separate reach and a
 * separate glance. Nine cells in a fixed arrangement can be learned once and
 * then played by feel, which is the only way a control anybody holds *while*
 * doing something else ever works.
 *
 * The layout puts the thing you hold longest — the throttle — down the middle
 * where the thumb rests, and translation on the sides where it rocks.
 *
 *     roll ↺    throttle ↑   roll ↻
 *     strafe ←  throttle ↓   strafe →
 *     down ⤓    up ⤒
 *
 * ── The corner is empty on purpose ──────────────────────────────────────────
 *
 * It held confirm, which is one key doing two jobs: tapped it sets a ship down,
 * held it commits a jump. Both now have a button of their own, at the place the
 * thing is actually happening — the dock panel over the pad, and the jump prompt
 * under the marker. That is a better home for either than a corner cell which
 * gave no clue which of the two it was about to do.
 *
 * Losing it bought the grid a row of height. The empty corner is the part that
 * would otherwise sit on the console, so the pad can drop into the console's own
 * band as long as the two *filled* cells beside it stay clear of it — see
 * `dropped`.
 *
 * ── What is missing, deliberately ───────────────────────────────────────────
 *
 * The taps — cruise, jump, warp, gear, thruster set, view, seat — are not here.
 * They are switches rather than controls, they are already buttons on the HUD,
 * and a phone can afford to make you stop flying for a moment to throw one. A
 * thumb can hold a grid this size; a fourth row is a menu.
 */

import { useEffect, useRef, useState } from 'react';

import type { Action } from '@interspacial/shared';

import {
  CONSOLE_WIDTH,
  HUD_BACKING,
  HUD_DIM,
  HUD_EDGE,
  HUD_TEXT,
  useConsoleClearance,
} from './FlightHud';
import { TOUCH_UI } from './hudScale';

export interface TouchControlsProps {
  /** A cell has been pressed or released. Held for as long as it is down. */
  onHold: (action: Action, down: boolean) => void;
}

/** What each cell does, in reading order. */
const CELLS: { action: Action; label: string; hint: string }[] = [
  { action: 'rollLeft', label: '↺', hint: 'roll' },
  { action: 'throttleUp', label: '▲', hint: 'throttle' },
  { action: 'rollRight', label: '↻', hint: 'roll' },

  { action: 'strafeLeft', label: '◄', hint: 'strafe' },
  { action: 'throttleDown', label: '▼', hint: 'throttle' },
  { action: 'strafeRight', label: '►', hint: 'strafe' },

  { action: 'down', label: '⤓', hint: 'down' },
  { action: 'up', label: '⤒', hint: 'up' },
  // and the ninth slot stays empty — see above.
];

/** Cell size, the gap between cells, and the pad's inset from the corner. */
const CELL = 58;
const GAP = 4;
const EDGE = 12;

/**
 * The right edge of the last *filled* cell in the bottom row.
 *
 * The third column of that row is empty, so it is this — rather than the pad's
 * own width — that has to clear the console before the pad may drop beside it.
 */
const FILLED_RIGHT = EDGE + CELL * 2 + GAP;

/**
 * How far the pad may drop into the console's band: one row, or none.
 *
 * Measured rather than assumed. The console is centred and the pad is in the
 * left corner, so whether the two can share a band is a question about *this*
 * screen's width — it works out on a modern phone held sideways and does not on
 * a small one, and the difference is a control sitting on the scanner.
 */
function dropped(viewport: number): number {
  const consoleEdge = viewport / 2 - CONSOLE_WIDTH / 2;
  return consoleEdge >= FILLED_RIGHT ? CELL + GAP : 0;
}

export function TouchControls({ onHold }: TouchControlsProps) {
  /*
   * Re-measured on resize, which on a phone means every rotation.
   *
   * Portrait is exactly the case the drop does not survive — the console is
   * wider than the screen there — so a width read once at load would be wrong
   * for half of the ways the device can be held.
   */
  const [viewport, setViewport] = useState(() => window.innerWidth);

  // Watched, not assumed: the console grows a line when a route is set, and the
  // grid has to move with it rather than through it.
  const clearance = useConsoleClearance();

  /*
   * Everything let go of on the way out.
   *
   * The grid is drawn only while the instruments are lit, so a jump unmounts it
   * — and a jump is entered by holding the throttle, which means there is very
   * often a cell down at the moment it goes. Its pointer-up then lands on
   * nothing and the action stays held for the rest of the flight.
   *
   * Through a ref because the caller passes an inline arrow: as a dependency it
   * would tear this down and rebuild it every frame, and a cleanup that runs
   * every frame would release the cell being pressed.
   */
  const release = useRef(onHold);
  release.current = onHold;

  useEffect(
    () => () => {
      for (const cell of CELLS) release.current(cell.action, false);
    },
    [],
  );

  useEffect(() => {
    const measure = () => setViewport(window.innerWidth);

    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
    };
  }, []);

  return (
    <div
      style={{
        position: 'absolute',
        // Left, so the thumb holding the controls is not the one covering the
        // reticle — and so it is clear of the radio, which keeps the right.
        left: EDGE,
        /*
         * Above the console, and higher than it strictly needs to be.
         *
         * The grid was at 12 and sat on the console: the console is centred, but
         * on a phone it is most of the screen wide, so "centred" reaches into
         * the left corner. `CONSOLE_CLEARANCE` is the panel's own top edge,
         * derived from the numbers it is drawn with, so this cannot drift out of
         * agreement with it the next time the padding moves.
         *
         * The ten points on top are air rather than arithmetic — and the lift is
         * wanted anyway. A thumb wrapped around the side of a phone rests around
         * a third of the way up; controls in the very corner are held by
         * stretching down to them, which is the position a hand tires in.
         */
        bottom: clearance + 10 - dropped(viewport),
        display: 'grid',
        gridTemplateColumns: `repeat(3, ${CELL}px)`,
        gridTemplateRows: `repeat(3, ${CELL}px)`,
        gap: GAP,
        // The grid itself is not a target; only the cells are, so the gaps let
        // whatever is behind them through rather than eating a stray thumb.
        pointerEvents: 'none',
        zIndex: 30,
      }}
    >
      {CELLS.map((cell) => {
        return (
          <button
            key={cell.action}
            type="button"
            /*
             * Pointer events rather than touch events, and captured.
             *
             * Capturing means a thumb that slides off the cell still releases
             * it — without that, rolling a thumb off the edge of a button leaves
             * the control held down and the ship rolling on its own, which is
             * the single worst failure a control like this has.
             */
            onPointerDown={(event) => {
              event.preventDefault();
              event.currentTarget.setPointerCapture(event.pointerId);
              onHold(cell.action, true);
            }}
            onPointerUp={(event) => {
              event.preventDefault();
              onHold(cell.action, false);
            }}
            onPointerCancel={() => onHold(cell.action, false)}
            // A control being held is not a thing to select or magnify.
            style={{
              pointerEvents: 'auto',
              touchAction: 'none',
              userSelect: 'none',
              WebkitUserSelect: 'none',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              background: HUD_BACKING,
              border: `1px solid ${HUD_EDGE}`,
              color: HUD_TEXT,
              font: 'inherit',
              fontSize: 17,
              lineHeight: 1,
              cursor: 'pointer',
            }}
          >
            <span>{cell.label}</span>
            <span style={{ fontSize: 7.5, letterSpacing: 0.6, color: HUD_DIM }}>{cell.hint}</span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * Whether this is a device flown with fingers.
 *
 * `pointer: coarse` rather than "is there a touchscreen": a laptop with a touch
 * display is still flown with a mouse, and putting a thumb grid over its cockpit
 * would be answering a question nobody asked. What matters is the *primary*
 * input, which is exactly what the media query reports.
 *
 * Answered in `hudScale`, which asks the same question to decide how big to draw
 * the instruments. One query, so the grid and the scale can never disagree about
 * what kind of device this is.
 */
export function coarsePointer(): boolean {
  return TOUCH_UI;
}
